import React from 'react';
import { Badge, Box, StatusIndicator } from '@amzn/awsui-components-react-v3';
import { SelectProps } from '@amzn/awsui-components-react-v3/polaris/select/interfaces';
import { StatusIndicatorProps } from '@amzn/awsui-components-react-v3/polaris/status-indicator/interfaces';
import { TableProps } from '@amzn/awsui-components-react-v3/polaris/table/interfaces';
import { SdMetric } from '../types/metrics.types';

// Coverage status severity. Values map 1:1 to Cloudscape StatusIndicator types.
export enum StatusType {
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
}

// Default "no category filter" option for the classification dropdown.
export const ALL_OPTION: SelectProps.Option = { label: 'All categories', value: 'all' };

// Rows shown per page in the summary table.
export const PAGE_SIZE = 10;

// Coverage thresholds: 95+ Excellent, 80+ Good, 70+ Needs attention, else Critical.
export const COVERAGE_THRESHOLDS = { EXCELLENT: 95, GOOD: 80, NEEDS_ATTENTION: 70 } as const;

// Coverage status labels + their StatusType, keyed by threshold bucket.
export const METRIC_STATUS: Record<'EXCELLENT' | 'GOOD' | 'NEEDS_ATTENTION' | 'CRITICAL', { text: string; type: StatusType }> = {
  EXCELLENT: { text: 'Excellent', type: StatusType.SUCCESS },
  GOOD: { text: 'Good', type: StatusType.SUCCESS },
  NEEDS_ATTENTION: { text: 'Needs attention', type: StatusType.WARNING },
  CRITICAL: { text: 'Critical', type: StatusType.ERROR },
};

// Bar chart sizing: a floor height plus a per-bar increment so the chart grows with the data.
export const CHART_MIN_HEIGHT = 320;
export const CHART_BAR_ROW_HEIGHT = 24;

// Coverage percentages always render on a fixed 0-100 axis.
export const COVERAGE_PERCENT_DOMAIN: [number, number] = [0, 100];

// Responsive layout for the count/status cards: 2 per row, 4 on wider viewports.
export const BLOCK_CARDS_PER_ROW = [{ cards: 2 }, { minWidth: 500, cards: 4 }];

// Table filter row split: text filter takes 8/12 columns, classification select 4/12.
export const FILTER_GRID_DEFINITION = [{ colspan: 8 }, { colspan: 4 }];

// Placeholder shown for a cell with no applicable value.
const EMPTY_CELL = '—';

// Format a metric's value based on its type.
export const valueText = (m: SdMetric): string => {
  if (m.value === null) return 'N/A';
  if (m.valueType === 'PERCENTAGE') return `${m.value.toFixed(1)}%`;
  if (m.valueType === 'NUMBER') return m.value.toLocaleString('en-US');
  return 'N/A';
};

// Status only applies to coverage percentages; everything else has none.
export const getStatus = (m: SdMetric): { text: string; type: StatusType } | null => {
  if (m.valueType !== 'PERCENTAGE' || m.value === null) return null;
  if (m.value >= COVERAGE_THRESHOLDS.EXCELLENT) return METRIC_STATUS.EXCELLENT;
  if (m.value >= COVERAGE_THRESHOLDS.GOOD) return METRIC_STATUS.GOOD;
  if (m.value >= COVERAGE_THRESHOLDS.NEEDS_ATTENTION) return METRIC_STATUS.NEEDS_ATTENTION;
  return METRIC_STATUS.CRITICAL;
};

// Column layout for the summary table. Static, so defined once at module scope.
export const SD_METRIC_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<SdMetric>[] = [
  { id: 'metricName', header: 'Metric name', cell: (m: SdMetric) => <Box fontWeight="bold">{m.metricName}</Box>, minWidth: 200, width: 240 },
  { id: 'description', header: 'Description', cell: (m: SdMetric) => m.description, minWidth: 220, width: 320 },
  { id: 'classification', header: 'Classification', cell: (m: SdMetric) => (m.metricClassification ? <Badge color="blue">{m.metricClassification}</Badge> : EMPTY_CELL), minWidth: 150, width: 180 },
  { id: 'valueType', header: 'Type', cell: (m: SdMetric) => m.valueType, minWidth: 110, width: 130 },
  { id: 'value', header: 'Value', cell: (m: SdMetric) => <Box fontWeight="bold">{valueText(m)}</Box>, minWidth: 100, width: 120 },
  {
    id: 'status',
    header: 'Status',
    cell: (m: SdMetric) => {
      const s = getStatus(m);
      // StatusType enum values equal Cloudscape's string-literal union members 1:1;
      // assert to satisfy the StatusIndicator prop type.
      return s ? <StatusIndicator type={s.type as StatusIndicatorProps.Type}>{s.text}</StatusIndicator> : EMPTY_CELL;
    },
    minWidth: 130,
    width: 150,
  },
];
